import React, { useMemo } from 'react';
import { Player } from '@remotion/player';
import { AbsoluteFill, useCurrentFrame, useVideoConfig, random, interpolate } from 'remotion';
import { 
    Brain, ShieldCheck, Box, 
    FolderOpen, Terminal, Webhook, Layout,
    Cloud, Youtube, Database
} from 'lucide-react';

// --- CONSTANTS ---
const COLORS = {
    INPUT: '#a1a1aa',   // Zinc-400
    CORE: '#ffffff',    // White
    AI: '#3b82f6',      // Blue
    GATE: '#f97316',    // Orange
    OUTPUT: '#a855f7',  // Purple
};

const INPUT_CHANNELS = [
    { id: 'watch', label: 'Watch Folder', icon: FolderOpen, sub: 'S3 / GCS' },
    { id: 'sdk', label: 'SDK / CLI', icon: Terminal, sub: 'Python / Node' },
    { id: 'hook', label: 'Webhook', icon: Webhook, sub: 'Event Trigger' },
    { id: 'ui', label: 'Vild UI', icon: Layout, sub: 'Manual Run' },
];

const OUTPUT_CHANNELS = [
    { id: 'cloud', label: 'Object Store', icon: Cloud, sub: 'S3 / R2' },
    { id: 'social', label: 'Social', icon: Youtube, sub: 'YT / TikTok' },
    { id: 'hook_out', label: 'Webhook', icon: Webhook, sub: 'Callback' },
    { id: 'db', label: 'Metadata', icon: Database, sub: 'JSON / DB' },
];

// --- COMPONENTS ---

// 1. Vertical Channel Stack (Inputs or Outputs)
const ChannelStack: React.FC<{ 
    x: number; 
    items: typeof INPUT_CHANNELS; 
    side: 'left' | 'right'; 
    activeIndices: number[];
}> = ({ x, items, side, activeIndices }) => {
    return (
        <div style={{
            position: 'absolute',
            left: x,
            top: '50%',
            transform: 'translate(-50%, -50%)',
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
            zIndex: 10
        }}>
            {items.map((item, index) => {
                const isActive = activeIndices.includes(index);
                return (
                    <div key={item.id} className="relative flex items-center gap-3 transition-all duration-300"
                        style={{ 
                            opacity: isActive ? 1 : 0.3,
                            flexDirection: side === 'left' ? 'row' : 'row-reverse',
                            transform: `scale(${isActive ? 1.05 : 1})`
                        }}
                    >
                        {/* Icon Box */}
                        <div style={{
                            width: 40, height: 40,
                            borderRadius: 8,
                            backgroundColor: isActive ? '#18181b' : 'transparent',
                            border: `1px solid ${isActive ? (side === 'left' ? COLORS.INPUT : COLORS.OUTPUT) : '#3f3f46'}`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            boxShadow: isActive ? `0 0 15px ${side === 'left' ? COLORS.INPUT : COLORS.OUTPUT}40` : 'none'
                        }}>
                            <item.icon size={20} color={isActive ? (side === 'left' ? '#fff' : '#fff') : '#71717a'} />
                        </div>

                        {/* Text */}
                        <div style={{ textAlign: side === 'left' ? 'left' : 'right' }}>
                            <div className="text-[10px] font-bold text-zinc-300 uppercase tracking-wide">{item.label}</div>
                            <div className="text-[8px] font-mono text-zinc-600">{item.sub}</div>
                        </div>

                        {/* Active Dot indicator */}
                        {isActive && (
                            <div style={{
                                position: 'absolute',
                                [side === 'left' ? 'right' : 'left']: -12,
                                width: 4, height: 4,
                                borderRadius: '50%',
                                backgroundColor: side === 'left' ? COLORS.INPUT : COLORS.OUTPUT,
                                boxShadow: `0 0 8px ${side === 'left' ? COLORS.INPUT : COLORS.OUTPUT}`
                            }} />
                        )}
                    </div>
                )
            })}
        </div>
    )
}

// 2. The Convergent Runtime Reactor
const ConvergentReactor: React.FC<{ x: number; y: number }> = ({ x, y }) => {
    const frame = useCurrentFrame();

    return (
        <div style={{
            position: 'absolute', left: x, top: y,
            transform: 'translate(-50%, -50%)',
            width: 260, height: 260,
            zIndex: 20,
            display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
            {/* Layer 3: Human/Gate Ring (Orange) - Outer Orbit */}
            <div style={{
                position: 'absolute', inset: 0,
                borderRadius: '50%',
                border: `1px solid ${COLORS.GATE}30`, 
                borderTopColor: COLORS.GATE,
                transform: `rotate(${frame * 0.2}deg)`,
                boxShadow: `0 0 40px ${COLORS.GATE}05`
            }}>
                <div style={{
                    position: 'absolute', top: '14.6%', left: '85.4%',
                    transform: `translate(-50%, -50%) rotate(-${frame * 0.2}deg)`
                }}>
                    <div className="bg-zinc-950 border border-orange-500/50 p-1.5 rounded-full shadow-[0_0_15px_rgba(249,115,22,0.5)]">
                        <ShieldCheck size={14} className="text-orange-500" />
                    </div>
                </div>
            </div>

            {/* Layer 2: AI Ring (Blue) - Middle Orbit */}
            <div style={{
                position: 'absolute', inset: 50,
                borderRadius: '50%',
                border: `1px solid ${COLORS.AI}30`,
                borderBottomColor: COLORS.AI,
                transform: `rotate(-${frame * 0.3}deg)`,
                boxShadow: `0 0 40px ${COLORS.AI}05`
            }}>
                 <div style={{
                    position: 'absolute', bottom: '14.6%', left: '14.6%',
                    transform: `translate(-50%, -50%) rotate(${frame * 0.3}deg)`
                }}>
                    <div className="bg-zinc-950 border border-blue-500/50 p-1.5 rounded-full shadow-[0_0_15px_rgba(59,130,246,0.5)]">
                        <Brain size={14} className="text-blue-500" />
                    </div>
                </div>
            </div>

            {/* Layer 1: The Core Reactor (Restored "Spinning Rings" design) */}
            <div style={{
                position: 'absolute',
                width: 100, height: 100,
                display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
                 {/* 3 Interlocking Rings */}
                 {[0, 1, 2].map(i => (
                    <div key={i} style={{
                        position: 'absolute',
                        inset: i * 8, // 0, 8, 16 spacing
                        borderRadius: '50%',
                        border: '1px solid rgba(255,255,255,0.1)',
                        borderTopColor: i === 0 ? COLORS.OUTPUT : i === 1 ? COLORS.CORE : COLORS.AI, // Varied colors
                        transform: `rotate(${frame * (i % 2 === 0 ? 1 : -1) * (1 + i) * 0.5}deg)`,
                        opacity: 0.8
                    }} />
                ))}

                {/* Center Mass */}
                <div className="relative z-10 w-14 h-14 rounded-full bg-zinc-900 border border-zinc-700 flex items-center justify-center shadow-[0_0_30px_rgba(255,255,255,0.2)]">
                    <Box size={20} className="text-white" />
                </div>
            </div>

            <div className="absolute -bottom-16 text-[10px] font-bold text-zinc-500 tracking-[0.2em]">VILD RUNTIME</div>
        </div>
    )
}

// 3. The Flowing Particles
interface ParticleProps {
    startX: number;
    startY: number;
    endX: number;
    endY: number;
    centerX: number;
    centerY: number;
    delay: number;
    color: string;
}

const Particle: React.FC<ParticleProps> = ({ startX, startY, endX, endY, centerX, centerY, delay, color }) => {
    const frame = useCurrentFrame();
    const activeFrame = frame - delay;
    const duration = 70; // Total travel time

    if (activeFrame < 0 || activeFrame > duration) return null;

    const progress = activeFrame / duration;

    // Path Logic: Start -> Center -> End
    // We use a multi-stage lerp for a "hub and spoke" feel
    let x, y;

    if (progress < 0.5) {
        // Phase 1: Input to Center
        const t = progress * 2; // 0 to 1
        // Ease out quad
        const ease = 1 - (1 - t) * (1 - t);
        x = interpolate(ease, [0, 1], [startX, centerX]);
        y = interpolate(ease, [0, 1], [startY, centerY]);
    } else {
        // Phase 2: Center to Output
        const t = (progress - 0.5) * 2; // 0 to 1
        // Ease in quad
        const ease = t * t;
        x = interpolate(ease, [0, 1], [centerX, endX]);
        y = interpolate(ease, [0, 1], [centerY, endY]);
    }

    // Scale effect (shrink at center)
    const scale = interpolate(Math.abs(progress - 0.5), [0, 0.5], [0.5, 1]);
    const opacity = progress > 0.9 ? 1 - (progress - 0.9) * 10 : progress < 0.1 ? progress * 10 : 1;

    return (
        <div style={{
            position: 'absolute',
            left: x,
            top: y,
            transform: `translate(-50%, -50%) scale(${scale})`,
            zIndex: 15,
            opacity
        }}>
             {/* Trail */}
             <div className="absolute right-0 top-1/2 -translate-y-1/2 h-[1px] w-12 origin-right opacity-50" 
                style={{ 
                    background: `linear-gradient(90deg, transparent, ${color})`,
                    transform: `rotate(${Math.atan2(y - (progress < 0.5 ? centerY : endY), x - (progress < 0.5 ? centerX : endX))}rad)`
                }} 
             />
            <div style={{
                width: 6, height: 6,
                borderRadius: '50%',
                backgroundColor: color,
                boxShadow: `0 0 10px ${color}`,
            }} />
        </div>
    );
};

// --- MAIN COMPOSITION ---
const PipelineArchitecture = () => {
    const { width, height } = useVideoConfig();
    const frame = useCurrentFrame();

    // Coordinates
    const leftX = width * 0.18;
    const centerX = width * 0.5;
    const centerY = height * 0.5;
    const rightX = width * 0.82;

    // Helper to calculate Y position of an item in the stack
    const getStackY = (index: number, total: number) => {
        const spacing = 60; 
        const totalHeight = (total - 1) * spacing;
        const startY = centerY - (totalHeight / 2);
        return startY + (index * spacing);
    };

    // SEQUENCE GENERATION
    // We want a constant stream of "jobs" being processed
    const sequence = useMemo(() => {
        const events = [];
        const interval = 25; // Frames between jobs
        for (let i = 0; i < 50; i++) {
            // Pick random input and output
            const inIdx = Math.floor(random(i) * INPUT_CHANNELS.length);
            const outIdx = Math.floor(random(i + 100) * OUTPUT_CHANNELS.length);
            
            events.push({
                id: i,
                delay: i * interval,
                inIdx,
                outIdx,
                // Assign a "flavor" based on randomness (Standard, AI-heavy, Gate-heavy)
                flavor: random(i + 200) > 0.5 ? COLORS.INPUT : COLORS.OUTPUT 
            });
        }
        return events;
    }, []);

    // Determine which inputs/outputs are currently "active" for highlighting
    const activeInputs = sequence
        .filter(s => frame >= s.delay && frame < s.delay + 30) // Active during start of particle
        .map(s => s.inIdx);

    const activeOutputs = sequence
        .filter(s => frame >= s.delay + 35 && frame < s.delay + 70) // Active during end of particle
        .map(s => s.outIdx);

    return (
        <AbsoluteFill className="bg-zinc-950 overflow-hidden">
            {/* Background Grid */}
            <div className="absolute inset-0 opacity-10" 
                style={{
                    backgroundImage: 'linear-gradient(#71717a 1px, transparent 1px), linear-gradient(90deg, #71717a 1px, transparent 1px)',
                    backgroundSize: '40px 40px',
                    transform: `translateY(${frame % 40}px)`
                }}
            />

            {/* Connecting Lines (Background) */}
            <svg className="absolute inset-0 w-full h-full pointer-events-none opacity-20">
                {INPUT_CHANNELS.map((_, i) => (
                    <line key={`l-${i}`} 
                        x1={leftX + 20} y1={getStackY(i, INPUT_CHANNELS.length)} 
                        x2={centerX - 60} y2={centerY} 
                        stroke={COLORS.INPUT} strokeWidth={1} 
                    />
                ))}
                {OUTPUT_CHANNELS.map((_, i) => (
                    <line key={`r-${i}`} 
                        x1={centerX + 60} y1={centerY} 
                        x2={rightX - 20} y2={getStackY(i, OUTPUT_CHANNELS.length)} 
                        stroke={COLORS.OUTPUT} strokeWidth={1} 
                    />
                ))}
            </svg>

            {/* Stacks */}
            <ChannelStack x={leftX} items={INPUT_CHANNELS} side="left" activeIndices={activeInputs} />
            <ChannelStack x={rightX} items={OUTPUT_CHANNELS} side="right" activeIndices={activeOutputs} />

            {/* Central Runtime */}
            <ConvergentReactor x={centerX} y={centerY} />

            {/* Particles */}
            {sequence.map(ev => (
                <Particle 
                    key={ev.id}
                    delay={ev.delay}
                    startX={leftX + 20}
                    startY={getStackY(ev.inIdx, INPUT_CHANNELS.length)}
                    centerX={centerX}
                    centerY={centerY}
                    endX={rightX - 20}
                    endY={getStackY(ev.outIdx, OUTPUT_CHANNELS.length)}
                    color={ev.flavor === COLORS.INPUT ? COLORS.INPUT : COLORS.OUTPUT}
                />
            ))}

            {/* Vignette */}
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,transparent_30%,#09090b_100%)] pointer-events-none"></div>

        </AbsoluteFill>
    );
}

export const HeroAnimation: React.FC = () => {
  return (
    <div className="w-full h-full rounded-xl overflow-hidden border border-zinc-800 bg-zinc-950 shadow-2xl relative group">
       <div className="absolute inset-0 z-10 pointer-events-none rounded-xl ring-1 ring-white/10"></div>
       
      <Player
        component={PipelineArchitecture}
        durationInFrames={900}
        compositionWidth={1000}
        compositionHeight={500}
        fps={30}
        style={{ width: '100%', height: '100%' }}
        controls={false}
        loop
        autoPlay
        allowFullscreen={false}
        clickToPlay={false}
      />
    </div>
  );
};